import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FloatingSaveButton from './components/floating-save-button';

interface DiscountData {
  id: string;
  // Standard Games
  diskon4D: string;
  diskon3D: string;
  diskon2D: string;
  // Colok Varieties
  diskonColokBebas: string;
  diskonColokBebas2D: string;
  diskonColokNaga: string;
  diskonColokJitu: string;
  diskonMacauShio: string;
  // Special Games
  diskonShio: string;
  diskonKombinasi: string;
  diskonTengahTepi: string;
  diskonDasar: string;
  diskon5050: string;
  diskonSilangHomo: string;
  diskonKembangKempis: string;
  // Macau Types
  diskon4DMacau: string;
  diskon3DMacau: string;
  diskon2DMacau: string;
  // Super Discounts
  superdiskon4DMacau: string;
  superdiskon3DMacau: string;
  superdiskon2DMacau: string;
  // Additional Macau Types
  diskonColokBebasMacau: string;
  diskonColokBebas2DMacau: string;
  diskonColokNagaMacau: string;
  diskonColokJituMacau: string;
  diskonShioMacau: string;
  diskonKombinasiMacau: string;
  diskonTengahTepiMacau: string;
  diskonDasarMacau: string;
  diskon5050Macau: string;
  diskonSilangHomoMacau: string;
  diskonKembangKempisMacau: string;
  diskonMacauShioMacau: string;
}

const DiscountField = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => {
  return (
    <div className='flex flex-col'>
      <label className='block text-blue-400 text-sm font-medium mb-2'>{label}</label>
      <div className='relative'>
        <input
          type='text'
          value={value}
          onChange={e => onChange(e.target.value)}
          className='w-full h-[45px] bg-[#212a40] text-white border border-gray-600 rounded-md px-4 focus:outline-none focus:border-blue-500'
        />
        <div className='absolute inset-y-0 right-0 flex items-center pointer-events-none pr-3'>
          <span className='text-gray-400'>%</span>
        </div>
      </div>
    </div>
  );
};

const DiscountCard = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className='bg-[#1c243b] rounded-lg shadow-lg overflow-hidden '>
      <div className='bg-[#212a40] px-4 py-3 border-b border-[#2d3649]'>
        <h3 className='text-md font-medium text-white'>{title}</h3>
      </div>
      <div className='p-5 grid grid-cols-2 gap-5'>{children}</div>
    </div>
  );
};

const Discount = () => {
  const [discountData, setDiscountData] = useState<DiscountData>({
    id: '',
    diskon4D: '',
    diskon3D: '',
    diskon2D: '',
    diskonColokBebas: '',
    diskonColokBebas2D: '',
    diskonColokNaga: '',
    diskonColokJitu: '',
    diskonShio: '',
    diskonKombinasi: '',
    diskonTengahTepi: '',
    diskonDasar: '',
    diskon5050: '',
    diskonSilangHomo: '',
    diskonKembangKempis: '',
    diskonMacauShio: '',
    diskon4DMacau: '',
    diskon3DMacau: '',
    diskon2DMacau: '',
    superdiskon4DMacau: '',
    superdiskon3DMacau: '',
    superdiskon2DMacau: '',
    diskonColokBebasMacau: '',
    diskonColokBebas2DMacau: '',
    diskonColokNagaMacau: '',
    diskonColokJituMacau: '',
    diskonShioMacau: '',
    diskonKombinasiMacau: '',
    diskonTengahTepiMacau: '',
    diskonDasarMacau: '',
    diskon5050Macau: '',
    diskonSilangHomoMacau: '',
    diskonKembangKempisMacau: '',
    diskonMacauShioMacau: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: keyof DiscountData, value: string) => {
    setDiscountData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const fetchDiscountData = async () => {
      try {
        const response = await fetch(`/api/cms/get-data-discount`);
        const res = await response.json();
        if (res.data) {
          setDiscountData(res.data);
        }
      } catch (error) {
        toast.error('Gagal mengambil data diskon');
      }
    };

    fetchDiscountData();
  }, []);

  const save = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/cms/update-discount`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(discountData),
      });

      const res = await response.json();
      if (!response.ok) {
        throw new Error(res.errors?.[0]?.message || res.message || 'Gagal mengupdate data DISKON');
      }

      toast.success('Berhasil mengupdate data DISKON');
    } catch (error: any) {
      toast.error(error.message || 'Gagal mengupdate data DISKON');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='mx-auto'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
        {/* Standard  */}
        <DiscountCard title='STANDARD 4D3D2D'>
          <DiscountField label='4D' value={discountData.diskon4D} onChange={value => handleChange('diskon4D', value)} />
          <DiscountField label='3D' value={discountData.diskon3D} onChange={value => handleChange('diskon3D', value)} />
          <DiscountField label='2D' value={discountData.diskon2D} onChange={value => handleChange('diskon2D', value)} />
        </DiscountCard>

        {/* Macau  */}
        <DiscountCard title='MACAU 4D3D2D'>
          <DiscountField
            label='Macau 4D'
            value={discountData.diskon4DMacau}
            onChange={value => handleChange('diskon4DMacau', value)}
          />
          <DiscountField
            label='Macau 3D'
            value={discountData.diskon3DMacau}
            onChange={value => handleChange('diskon3DMacau', value)}
          />
          <DiscountField
            label='Macau 2D'
            value={discountData.diskon2DMacau}
            onChange={value => handleChange('diskon2DMacau', value)}
          />
        </DiscountCard>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
        {/* Super Discounts */}
        <DiscountCard title='SUPER DISCOUNT MACAU 4D3D2D'>
          <DiscountField
            label='Super 4D'
            value={discountData.superdiskon4DMacau}
            onChange={value => handleChange('superdiskon4DMacau', value)}
          />
          <DiscountField
            label='Super 3D'
            value={discountData.superdiskon3DMacau}
            onChange={value => handleChange('superdiskon3DMacau', value)}
          />
          <DiscountField
            label='Super 2D'
            value={discountData.superdiskon2DMacau}
            onChange={value => handleChange('superdiskon2DMacau', value)}
          />
        </DiscountCard>

        {/* Colok */}
        <DiscountCard title='COLOK'>
          <DiscountField
            label='Colok Bebas'
            value={discountData.diskonColokBebas}
            onChange={value => handleChange('diskonColokBebas', value)}
          />
          <DiscountField
            label='Colok Bebas 2D'
            value={discountData.diskonColokBebas2D}
            onChange={value => handleChange('diskonColokBebas2D', value)}
          />
          <DiscountField
            label='Colok Naga'
            value={discountData.diskonColokNaga}
            onChange={value => handleChange('diskonColokNaga', value)}
          />
          <DiscountField
            label='Colok Jitu'
            value={discountData.diskonColokJitu}
            onChange={value => handleChange('diskonColokJitu', value)}
          />
        </DiscountCard>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
        <DiscountCard title='GAMES - 1'>
          <DiscountField
            label='Tengah Tepi'
            value={discountData.diskonTengahTepi}
            onChange={value => handleChange('diskonTengahTepi', value)}
          />
          <DiscountField label='Dasar' value={discountData.diskonDasar} onChange={value => handleChange('diskonDasar', value)} />
          <DiscountField label='50-50' value={discountData.diskon5050} onChange={value => handleChange('diskon5050', value)} />
          <DiscountField label='Shio' value={discountData.diskonShio} onChange={value => handleChange('diskonShio', value)} />
        </DiscountCard>

        <DiscountCard title='GAMES - 2'>
          <DiscountField
            label='Silang Homo'
            value={discountData.diskonSilangHomo}
            onChange={value => handleChange('diskonSilangHomo', value)}
          />
          <DiscountField
            label='Kembang'
            value={discountData.diskonKembangKempis}
            onChange={value => handleChange('diskonKembangKempis', value)}
          />
          <DiscountField
            label='Kombinasi'
            value={discountData.diskonKombinasi}
            onChange={value => handleChange('diskonKombinasi', value)}
          />
        </DiscountCard>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
        <DiscountCard title='MACAU GAMES - 1'>
          <DiscountField
            label='Colok Bebas Macau'
            value={discountData.diskonColokBebasMacau}
            onChange={value => handleChange('diskonColokBebasMacau', value)}
          />
          <DiscountField
            label='Colok Bebas 2D Macau'
            value={discountData.diskonColokBebas2DMacau}
            onChange={value => handleChange('diskonColokBebas2DMacau', value)}
          />
          <DiscountField
            label='Colok Naga Macau'
            value={discountData.diskonColokNagaMacau}
            onChange={value => handleChange('diskonColokNagaMacau', value)}
          />
          <DiscountField
            label='Colok Jitu Macau'
            value={discountData.diskonColokJituMacau}
            onChange={value => handleChange('diskonColokJituMacau', value)}
          />
          <DiscountField
            label='Shio Macau'
            value={discountData.diskonShioMacau}
            onChange={value => handleChange('diskonShioMacau', value)}
          />
          <DiscountField
            label='Macau Shio Macau'
            value={discountData.diskonMacauShioMacau}
            onChange={value => handleChange('diskonMacauShioMacau', value)}
          />
        </DiscountCard>

        <DiscountCard title='MACAU GAMES - 2'>
          <DiscountField
            label='Tengah Tepi Macau'
            value={discountData.diskonTengahTepiMacau}
            onChange={value => handleChange('diskonTengahTepiMacau', value)}
          />
          <DiscountField
            label='Dasar Macau'
            value={discountData.diskonDasarMacau}
            onChange={value => handleChange('diskonDasarMacau', value)}
          />
          <DiscountField
            label='50-50 Macau  '
            value={discountData.diskon5050Macau}
            onChange={value => handleChange('diskon5050Macau', value)}
          />
          <DiscountField
            label='Silang Homo Macau'
            value={discountData.diskonSilangHomoMacau}
            onChange={value => handleChange('diskonSilangHomoMacau', value)}
          />
          <DiscountField
            label='Kembang Macau'
            value={discountData.diskonKembangKempisMacau}
            onChange={value => handleChange('diskonKembangKempisMacau', value)}
          />
          <DiscountField
            label='Kombinasi Macau'
            value={discountData.diskonKombinasiMacau}
            onChange={value => handleChange('diskonKombinasiMacau', value)}
          />
        </DiscountCard>
      </div>

      {/* Save Button */}
      <FloatingSaveButton onClick={save} isLoading={isLoading} />
    </div>
  );
};

export default Discount;
